import { installIndexedFixtureState, type IndexedFixtureState } from './deltaState';
import type {
  ActiveEffect,
  ControllerMirror,
  ControllerMirrorPatch,
  LiveState,
  RigSnapshot,
  RouteName,
} from './types';

export const AUTHORED_NODE_SCHEMA = 'controller-authored-node/1';

export interface AuthoredNode<T = unknown> {
  schema: typeof AUTHORED_NODE_SCHEMA;
  revision: number;
  value: T;
  lastChange?: Record<string, unknown>;
}

export interface AuthoredTreeModel {
  state?: LiveState;
  panelFixtures?: LiveState['fixtures'];
  mirror: ControllerMirrorPatch;
  revisions: Record<string, number>;
  fixtureCount?: number;
  rigRevision?: string;
  /**
   * The RUN of the daemon these revisions belong to, as the Pi itself names it in the `meta` node.
   *
   * The Pi persists its authored revision counter and stamps a new opaque epoch every boot, so a change
   * of this string is the room saying "different run" in its own words rather than the console inferring
   * it. The two inference signals are kept beside it (see `authoredRunRestart`), because either one
   * can be the only thing a given console observes.
   */
  epoch?: string;
  favoriteSceneIds?: string[];
  /**
   * The fixture indices whose `panel/fixtures/NNNNNN` node has arrived in this run, in ascending order.
   *
   * The controller publishes each fixture as its own node and the writes land in whatever order the
   * network allows — seconds apart on a gate open. A fixture missing from this list has NO reported level;
   * it is unknown, never 0. See `authoredPanelBaselineComplete`.
   */
  arrivedFixtures?: number[];
  /**
   * The last revision each FEED delivered for each path, beside the combined gate in `revisions`.
   *
   * A linked console follows the tree on two feeds — the cloud listener and the room's session link — and
   * they do not number an unchanged node alike: the link's snapshot stamps every node with the revision
   * current when the link joined, the cloud keeps each node at the revision it last changed, and the link
   * echoes a change ~150 ms after it happens while the cloud copy lands 0.3–9 s later. A node arriving from
   * one feed below what the OTHER feed delivered is therefore normal and is a stale `duplicate`; only a
   * feed going backwards against ITSELF says the room's counter started over (`authoredRunRestart`).
   * The link's map belongs to one link session: a new session starts a new map.
   */
  feedRevisions?: AuthoredFeedRevisions;
}

/** Which feed delivered an authored node: the cloud listener, or one session of the room's link. */
export type AuthoredFeed = { kind: 'cloud' } | { kind: 'link'; session: string };

export interface AuthoredFeedRevisions {
  cloud?: Record<string, number>;
  link?: { session: string; revisions: Record<string, number> };
}

/** What this feed last delivered for each path; empty for a link session not seen before. */
function feedRevisionsOf(model: AuthoredTreeModel, feed: AuthoredFeed): Record<string, number> {
  const held = model.feedRevisions;
  if (feed.kind === 'cloud') return held?.cloud ?? {};
  return held?.link?.session === feed.session ? held.link.revisions : {};
}

/** The model with `revision` recorded as the last one `feed` delivered for `path`. */
function withFeedRevision(model: AuthoredTreeModel, feed: AuthoredFeed, path: string, revision: number): AuthoredTreeModel {
  const revisions = { ...feedRevisionsOf(model, feed), [path]: revision };
  return {
    ...model,
    feedRevisions: feed.kind === 'cloud'
      ? { ...model.feedRevisions, cloud: revisions }
      : { ...model.feedRevisions, link: { session: feed.session, revisions } },
  };
}

export interface AuthoredTreeReduction {
  model: AuthoredTreeModel;
  kind: 'applied' | 'duplicate' | 'invalid';
  error?: string;
}

/** Structural match for the parity harness' revisioned NodeObserver seam. */
export interface RevisionedAuthoredObservation {
  value: unknown;
  revision: number;
  lastChange?: Record<string, unknown>;
}

export interface AuthoredNodeObserver {
  observe(path: string, node: RevisionedAuthoredObservation | null): void;
  snapshot(): unknown;
}

export function authoredDomainPathsForRoute(route: RouteName): string[] {
  const paths = new Set(['meta', 'core']);
  if (route === 'scenes') {
    paths.add('scenes');
    paths.add('panel/meta');
  } else if (route === 'effects') {
    paths.add('effects');
    paths.add('music');
  } else if (route === 'music') {
    paths.add('music');
    paths.add('effects');
  } else if (route === 'scores') {
    paths.add('transport');
  } else if (route === 'status') {
    paths.add('transport');
    paths.add('effects');
    paths.add('sessions');
    paths.add('music');
  } else if (route === 'connect' || route === 'settings') {
    paths.add('sessions');
  }
  return [...paths];
}

export function authoredFixturePath(index: number): string {
  if (!Number.isSafeInteger(index) || index < 0) throw new Error('Fixture index must be non-negative.');
  return `panel/fixtures/${String(index).padStart(6, '0')}`;
}

function isRecord(value: unknown): value is Record<string, any> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function parseAuthoredNode(value: unknown): AuthoredNode | undefined {
  if (!isRecord(value) || value.schema !== AUTHORED_NODE_SCHEMA) return undefined;
  if (!Number.isSafeInteger(value.revision) || Number(value.revision) < 0) return undefined;
  // Realtime Database elides explicit nulls, including a value:null tombstone and an object whose
  // descendants are all null. The revision envelope remains and is therefore the durable tombstone.
  return ('value' in value ? value : { ...value, value: null }) as AuthoredNode;
}

function initialState(rig?: RigSnapshot): LiveState {
  return {
    activeSource: 'panel',
    activeSourceLabel: 'Scenes',
    blackout: false,
    outputRateHz: 0,
    fixtures: [],
    transport: null,
    effects: [],
    scene: null,
    rigRevision: rig?.rigVersionId,
    stateRevision: 0,
  };
}

function primaryEffectSummary(active: unknown[]): ControllerMirror['effect'] {
  const values = active.filter(isRecord);
  const selected = values.find((effect) => ['audio_reactive', 'effect_package'].includes(String(effect.kind))) ?? values[0];
  if (!selected) return { running: false, count: 0 };
  return {
    running: true,
    count: values.length,
    effectId: String(selected.id ?? selected.effectId ?? ''),
    kind: String(selected.kind ?? ''),
    preset: selected.preset,
    devices: Number(selected.devices ?? 0),
    health: selected.health,
    stack: selected.stack,
    package: selected.package,
    sync: selected.sync == null ? undefined : {
      on: Boolean(selected.sync),
      mode: selected.clock?.mode,
      musicRate: selected.clock?.musicRate,
    },
  };
}

function restoreRTDBElidedEffectNulls(effect: unknown): unknown {
  if (!isRecord(effect) || !isRecord(effect.package)) return effect;
  const packageState = { ...effect.package };
  // The 1.7.20 package status is a complete struct and always carries both nested descriptors. Older or
  // deliberately minimal shapes do not, so do not invent nullable fields for those expand-window values.
  const completePackageStatus = isRecord(packageState.inputs) && isRecord(packageState.audioFeatures);
  if (completePackageStatus && !Object.prototype.hasOwnProperty.call(packageState, 'frameAgeMs')) {
    packageState.frameAgeMs = null;
  }
  if (isRecord(packageState.inputs) && !Object.prototype.hasOwnProperty.call(packageState.inputs, 'external')) {
    packageState.inputs = { ...packageState.inputs, external: null };
  }
  if (isRecord(packageState.audioFeatures)
      && !Object.prototype.hasOwnProperty.call(packageState.audioFeatures, 'consumes')) {
    packageState.audioFeatures = { ...packageState.audioFeatures, consumes: null };
  }
  return { ...effect, package: packageState };
}

/** The fixture index a `panel/fixtures/NNNNNN` path names, or undefined for any other path. */
export function authoredFixtureIndexOf(path: string): number | undefined {
  const match = /^panel\/fixtures\/(\d{6})$/.exec(path);
  return match ? Number(match[1]) : undefined;
}

/**
 * Whether every fixture the tree counts has arrived in this run — the room's panel baseline is whole.
 *
 * Until it is, the room has not reported its levels: a fixture whose node is still on its way would
 * otherwise be drawn, and captured as an Undo or scene "before", at a 0 nobody published. Before any
 * `meta`/`panel/meta` has said how many fixtures there are, nothing says the room is whole.
 */
export function authoredPanelBaselineComplete(model: AuthoredTreeModel): boolean {
  const count = model.fixtureCount;
  if (count === undefined || !Number.isSafeInteger(count)) return false;
  const arrived = new Set(model.arrivedFixtures ?? []);
  for (let index = 0; index < count; index += 1) if (!arrived.has(index)) return false;
  return true;
}

/**
 * A node that says the room has begun a new run, and what of the old one may be kept.
 *
 * `fromRevision` is set only when the room NAMED its new run (a changed `meta` epoch on a counter the
 * daemon persists across boots): fixtures held at or above it were already sent by the new run. When it is
 * absent, nothing held is kept — see `authoredRunRestart`.
 */
export interface AuthoredRunRestart {
  fromRevision?: number;
}

/**
 * The daemon's authored revisions are a per-RUN counter, so they can start over. This is the console
 * noticing that they have.
 *
 * The Pi's counter lives in memory. It is seeded above the stored tree's high-water at boot only when
 * that tree is still there — and it is deleted whenever the last watching console's lease lapses, which
 * on prod happened six times on 2026-09-17 alone. So: an operator closes the laptop, timers pause, the
 * lease lapses, the gate closes and the tree is deleted; the Pi restarts in that window and its counter
 * starts again at 1; the laptop wakes and the Pi republishes every leaf at revisions 1, 2, 3.
 *
 * The console remembered revisions in the hundreds, and `reduceAuthoredTreeNode` rejects anything not
 * greater than what it holds — correctly, as a guard against stale writes. The result was a console
 * showing a frozen pre-sleep room and silently discarding every leaf the room published afterwards,
 * including the operator's own edits, until the counter climbed back past the old high-water. Not
 * "briefly blank and refills" — stale indefinitely, with nothing said.
 *
 * THREE signals say so, and they are kept together because no one of them covers the field:
 *
 * 1. **The room says so.** The daemon persists its counter across boots and stamps a new opaque
 *    `epoch` into the `meta` envelope every time it starts, so a CHANGE of that string is the room
 *    naming its own new run. First sight of an epoch is not a change — a console that has only just
 *    attached has no run to be told it left.
 * 2. **A revision goes BACKWARDS on a path, on the SAME feed.** `onValue` carries the value at that path
 *    NOW, not a replay, so a lower number than the cloud listener itself delivered means the writer
 *    restarted its counter; likewise within one link session. This is what a console that slept through
 *    the whole gate-closed window observes, and it needs no epoch field at all. A lower number than the
 *    OTHER feed delivered is not this signal: the two feeds number unchanged nodes differently and arrive
 *    seconds apart (`AuthoredTreeModel.feedRevisions`), so that node is a stale copy and the reducer
 *    reads it as a `duplicate` (dev proof 2026-10-21, runs A/B: reading it as a restart forgot every
 *    fixture arrival, and a fixture that never changes is never resent).
 * 3. **`meta` disappears** (handled at VenueProvider's listener). The tree was deleted, so whatever
 *    comes back is a different run even when it is numbered identically.
 *
 * An epoch change is a restart on either feed — but only on a node at or above the revision held for its
 * path. The daemon that names its runs persists its counter, so its new run is numbered above the old; a
 * `meta` BELOW what is held that carries a different epoch is the other feed's late copy of the run this
 * console already left, and reading it as a restart would bounce the console back and forth between runs.
 *
 * 1 subsumes neither of the others: a daemon too old to publish an epoch still restarts, and a tree
 * that is deleted and republished on the SAME epoch by the SAME run is still a tree this console must
 * re-adopt. They cost nothing, and each one is the only signal some real console sees.
 *
 * The caller runs this BEFORE the reducer, which is not incidental: `reduceAuthoredTreeNode` drops a node
 * whose revision is not greater than the one held, so a `meta` that comes back on the number already
 * held — the same-numbers case — would never reach the branch that reads its epoch.
 *
 * What may be KEPT differs by signal, and the backwards case is checked first because it decides it.
 * A named epoch on a persisted counter numbers the new run ABOVE the old, so a fixture held at or above
 * the revealing revision is the new run's own (`fromRevision`). A revision that went backwards says the
 * counter started over: the old run's fixtures sit in the hundreds, above anything the new run has
 * reached, so "at or above" would keep every one of them as reported and the reset would do nothing.
 * There, nothing is kept — and since the revision gate is then empty, the revealing node and the rest of
 * the new run install normally.
 */
export function authoredRunRestart(
  model: AuthoredTreeModel,
  feed: AuthoredFeed,
  path: string,
  value: unknown,
): AuthoredRunRestart | undefined {
  const revision = (value as { revision?: unknown } | null | undefined)?.revision;
  const published = Number.isSafeInteger(revision) ? Number(revision) : undefined;
  const sameFeed = feedRevisionsOf(model, feed)[path];
  if (published !== undefined && Number.isSafeInteger(sameFeed) && published < sameFeed) return {};
  if (path === 'meta' || path === 'panel/meta') {
    const epoch = (value as { value?: { epoch?: unknown } } | null | undefined)?.value?.epoch;
    const held = model.revisions[path];
    const current = published === undefined || !Number.isSafeInteger(held) || published >= held;
    if (current && typeof epoch === 'string' && epoch && model.epoch && epoch !== model.epoch) {
      return { fromRevision: published };
    }
  }
  return undefined;
}

/** One feed's node through the restart check and the reducer — what VenueProvider installs. */
export interface AuthoredFeedReduction extends AuthoredTreeReduction {
  /** Set when the node said the room began a new run: the model after that reset, before the node. */
  restarted?: AuthoredTreeModel;
}

/**
 * One authored node from one feed, end to end: the restart signals, then the ONE reducer, then the feed's
 * own record of what it delivered (kept on a `duplicate` too — a stale copy from this feed still moves this
 * feed's position). Every arrival on every feed goes through here, so no caller can skip the per-feed rule.
 */
export function reduceAuthoredFeedNode(
  current: AuthoredTreeModel,
  feed: AuthoredFeed,
  path: string,
  input: unknown,
  rig?: RigSnapshot,
): AuthoredFeedReduction {
  // Before the reducer, which is not incidental: it drops a node whose revision is not greater than the
  // one held, so a `meta` that names a new run on the number already held would never be read.
  const restart = authoredRunRestart(current, feed, path, input);
  const restarted = restart ? beginAuthoredEpoch(current, restart.fromRevision, rig) : undefined;
  const reduced = reduceAuthoredTreeNode(restarted ?? current, path, input, rig);
  if (reduced.kind === 'invalid') return { ...reduced, ...(restarted ? { restarted } : {}) };
  const revision = Number((input as { revision: number }).revision);
  return {
    ...reduced,
    model: withFeedRevision(reduced.model, feed, path, revision),
    ...(restarted ? { restarted } : {}),
  };
}

/**
 * Forget which revisions this console has seen, so the room's new run can be adopted from scratch.
 *
 * The revision gate is dropped (the combined one and every feed's), and so is every fixture not kept (below). The model's own shape — `fixtureCount` above all,
 * which decides which per-fixture paths are subscribed to — is kept, because tearing that down would
 * unsubscribe the very listeners the republished leaves are about to arrive on.
 *
 * `fromRevision` is the revision of the `meta` that NAMED the new run, when one did. Every node of one
 * publish carries the same revision and the controller writes them in parallel, so a fixture of the new
 * run can land BEFORE the `meta` that names the run; a fixture held at or above that revision is kept as
 * arrived, because the controller has already sent it and will not send it again until it changes. With
 * no revision — the tree was deleted, or a revision went backwards (`authoredRunRestart`) — nothing held
 * belongs to the run that republishes it.
 *
 * A fixture that is not kept leaves the model's fixtures too, not only the arrivals: Scenes reads its
 * levels from `state.fixtures`, so an old-run fixture left there would go on being drawn, edited and
 * captured as an Undo "before" at a level the new run has not published. Without the rig a kept index
 * cannot be named, so nothing is kept.
 */
export function beginAuthoredEpoch(
  model: AuthoredTreeModel,
  fromRevision?: number,
  rig?: RigSnapshot,
): AuthoredTreeModel {
  const kept = fromRevision === undefined || !rig
    ? []
    : (model.arrivedFixtures ?? []).filter((index) => (
      (model.revisions[authoredFixturePath(index)] ?? -1) >= fromRevision
    ));
  return { ...keepOnlyFixtures(model, kept, rig), revisions: {}, feedRevisions: undefined };
}

/**
 * Forget one fixture's level because its node is gone — an RTDB deletion, which carries no revision.
 *
 * The fixture goes back to unknown, exactly as before its node first arrived: out of the arrivals and out
 * of the fixtures Scenes draws. The revision gate is left alone; a deletion is not a newer revision, and
 * the next node on this path is ordered against what was held (a lower one from the same feed is a new
 * run, see `authoredRunRestart`).
 */
export function forgetAuthoredFixture(
  model: AuthoredTreeModel,
  fixtureIndex: number,
  rig?: RigSnapshot,
): AuthoredTreeModel {
  const fixtureId = rig?.fixtures[fixtureIndex]?.deviceId;
  const isOther = (fixture: { deviceId: string }) => fixture.deviceId !== fixtureId;
  return {
    ...model,
    ...(fixtureId ? {
      state: model.state ? { ...model.state, fixtures: model.state.fixtures.filter(isOther) } : model.state,
      panelFixtures: model.panelFixtures?.filter(isOther),
    } : {}),
    // A removed fixture has no reported level, whether or not this console could name its device.
    arrivedFixtures: withArrival(model.arrivedFixtures, fixtureIndex, false),
  };
}

/** The model with only the given fixture indices still reported, in its arrivals AND its fixtures. */
function keepOnlyFixtures(
  model: AuthoredTreeModel,
  kept: readonly number[],
  rig: RigSnapshot | undefined,
): AuthoredTreeModel {
  const keptIds = new Set(kept.map((index) => rig?.fixtures[index]?.deviceId).filter(Boolean));
  const isKept = (fixture: { deviceId: string }) => keptIds.has(fixture.deviceId);
  return {
    ...model,
    state: model.state ? { ...model.state, fixtures: model.state.fixtures.filter(isKept) } : model.state,
    panelFixtures: model.panelFixtures?.filter(isKept),
    arrivedFixtures: [...kept],
  };
}

function withArrival(arrived: readonly number[] | undefined, index: number, present: boolean): number[] {
  const next = new Set(arrived ?? []);
  if (present) next.add(index);
  else next.delete(index);
  return [...next].sort((left, right) => left - right);
}

/**
 * Reduce one complete, independently revisioned tree node. Ordering is per path: a revision skipped by an
 * unrelated domain is normal, and a late callback can never roll its own domain backward.
 */
export function reduceAuthoredTreeNode(
  current: AuthoredTreeModel,
  path: string,
  input: unknown,
  rig?: RigSnapshot,
): AuthoredTreeReduction {
  const node = parseAuthoredNode(input);
  if (!node) return { model: current, kind: 'invalid', error: `Invalid authored node at ${path}.` };
  if ((current.revisions[path] ?? -1) >= node.revision) return { model: current, kind: 'duplicate' };
  // Relay and Pi writers retain a revisioned tombstone instead of deleting the RTDB leaf. That preserves
  // the greater-revision guard across late writes, so consumers must interpret value:null as removal.
  if (node.value === null) return removeAuthoredTreeNode(current, path, node.revision, rig);
  try {
    const revisions = { ...current.revisions, [path]: node.revision };
    let state = { ...(current.state ?? initialState(rig)) };
    let mirror = { ...current.mirror };
    let fixtureCount = current.fixtureCount;
    let rigRevision = current.rigRevision;
    let epoch = current.epoch;
    let favoriteSceneIds = current.favoriteSceneIds;
    const value = node.value;

    if (path === 'meta' || path === 'panel/meta') {
      if (!isRecord(value) || value.schema !== 'controller-authored-tree/1') throw new Error('Invalid authored tree metadata.');
      fixtureCount = Number(value.fixtureCount);
      rigRevision = String(value.rigRevision ?? '');
      // `meta` and `panel/meta` carry the same envelope byte for byte, so either one records the run.
      // A daemon that publishes no epoch leaves the previous value alone rather than clearing it: an
      // absent field is silence, not a new run.
      if (typeof value.epoch === 'string' && value.epoch) epoch = value.epoch;
      if (!Number.isSafeInteger(fixtureCount) || fixtureCount < 0) throw new Error('Invalid authored fixture count.');
      if (rig?.rigVersionId && rigRevision && rig.rigVersionId !== rigRevision) throw new Error('The authored state belongs to a different rig.');
      state.rigRevision = rigRevision || rig?.rigVersionId;
    } else if (path === 'core') {
      if (!isRecord(value)) throw new Error('Invalid authored core node.');
      state = {
        ...state,
        activeSource: String(value.activeSource ?? state.activeSource),
        activeSourceLabel: typeof value.activeSourceLabel === 'string' ? value.activeSourceLabel : state.activeSourceLabel,
        blackout: typeof value.blackout === 'boolean' ? value.blackout : state.blackout,
        outputRateHz: Number.isFinite(value.outputRateHz) ? Number(value.outputRateHz) : state.outputRateHz,
      };
      // core is a complete domain node. RTDB removes null-valued descendants from stored objects, so
      // absence of these nullable keys means clear, not "keep the previous rig/network object".
      mirror = { ...mirror, rig: null, rigError: null, network: null, ...value };
    } else if (path === 'transport') {
      const transport = isRecord(value) ? value as NonNullable<LiveState['transport']> : null;
      state.transport = transport;
      mirror.transport = transport;
    } else if (path === 'effects') {
      const domain = isRecord(value) ? value : { active: value };
      const active: ActiveEffect[] = Array.isArray(domain.active)
        ? domain.active.map(restoreRTDBElidedEffectNulls).filter(isRecord)
        : [];
      state.effects = active;
      mirror.effect = isRecord(domain.summary)
        ? domain.summary as NonNullable<ControllerMirror['effect']>
        : primaryEffectSummary(active);
    } else if (path === 'sessions') {
      if (!isRecord(value)) throw new Error('Invalid authored sessions node.');
      state.stream = isRecord(value.stream) ? value.stream : undefined;
      state.remoteLive = isRecord(value.remoteLive) ? value.remoteLive : undefined;
      mirror.stream = isRecord(value.stream)
        ? value.stream as NonNullable<ControllerMirror['stream']>
        : null;
      mirror.remoteLive = isRecord(value.remoteLive)
        ? value.remoteLive as NonNullable<ControllerMirror['remoteLive']>
        : null;
    } else if (path === 'music') {
      mirror.musicInput = isRecord(value)
        ? value as NonNullable<ControllerMirror['musicInput']>
        : null;
    } else if (path === 'scenes') {
      const scenes = isRecord(value) ? value : {};
      favoriteSceneIds = Array.isArray(scenes.favoriteSceneIds)
        ? scenes.favoriteSceneIds.filter((id): id is string => typeof id === 'string')
        : [];
      mirror.scenes = {
        recalledSceneId: typeof scenes.recalledSceneId === 'string' ? scenes.recalledSceneId : '',
        favoriteSceneIds,
      };
      state.scene = { recalledSceneId: mirror.scenes.recalledSceneId };
    } else {
      const fixtureIndex = authoredFixtureIndexOf(path);
      if (fixtureIndex == null || !rig) throw new Error(`Unknown authored node ${path}.`);
      const fixture = installIndexedFixtureState(value as IndexedFixtureState, fixtureIndex, rig);
      const fixtures = [...state.fixtures];
      const existing = fixtures.findIndex((item) => item.deviceId === fixture.deviceId);
      // delta-v1 materializes an empty elements array when a fixture-scoped update touches a conventional
      // one-part fixture. Preserve that observable transition after the baseline, while leaving the join's
      // omitted property omitted.
      if (existing >= 0) fixtures[existing] = fixture.elements === undefined
        ? { ...fixture, elements: [] }
        : fixture;
      else fixtures.push(fixture);
      fixtures.sort((left, right) => (
        rig.fixtures.findIndex((item) => item.deviceId === left.deviceId)
        - rig.fixtures.findIndex((item) => item.deviceId === right.deviceId)
      ));
      state.fixtures = fixtures;

      const panelFixtures = [...(current.panelFixtures ?? [])];
      const existingPanel = panelFixtures.findIndex((item) => item.deviceId === fixture.deviceId);
      if (existingPanel >= 0) panelFixtures[existingPanel] = fixture;
      else panelFixtures.push(fixture);
      panelFixtures.sort((left, right) => (
        rig.fixtures.findIndex((item) => item.deviceId === left.deviceId)
        - rig.fixtures.findIndex((item) => item.deviceId === right.deviceId)
      ));
      current = { ...current, panelFixtures, arrivedFixtures: withArrival(current.arrivedFixtures, fixtureIndex, true) };
    }

    state.stateRevision = Math.max(state.stateRevision ?? 0, node.revision);
    return {
      kind: 'applied',
      model: {
        ...current,
        state,
        mirror,
        revisions,
        fixtureCount,
        rigRevision,
        epoch,
        favoriteSceneIds,
      },
    };
  } catch (error) {
    return { model: current, kind: 'invalid', error: error instanceof Error ? error.message : String(error) };
  }
}

/** Apply a revisioned leaf deletion from a canonical command acknowledgement. */
export function removeAuthoredTreeNode(
  current: AuthoredTreeModel,
  path: string,
  revision: number,
  rig?: RigSnapshot,
): AuthoredTreeReduction {
  if (!Number.isSafeInteger(revision) || revision < 0) {
    return { model: current, kind: 'invalid', error: `Invalid authored deletion revision at ${path}.` };
  }
  if ((current.revisions[path] ?? -1) >= revision) return { model: current, kind: 'duplicate' };

  const fixtureIndex = authoredFixtureIndexOf(path);
  const revisions = { ...current.revisions, [path]: revision };
  let state = { ...(current.state ?? initialState(rig)) };
  const mirror = { ...current.mirror };
  let favoriteSceneIds = current.favoriteSceneIds;

  if (fixtureIndex != null) {
    // The same removal an RTDB deletion of the node goes through.
    current = forgetAuthoredFixture({ ...current, state }, fixtureIndex, rig);
    state = { ...(current.state ?? state) };
  } else if (path === 'transport') {
    state.transport = null;
    mirror.transport = null;
  } else if (path === 'effects') {
    state.effects = [];
    mirror.effect = { running: false, count: 0 };
  } else if (path === 'sessions') {
    state.stream = undefined;
    state.remoteLive = undefined;
    mirror.stream = null;
    mirror.remoteLive = null;
  } else if (path === 'music') {
    mirror.musicInput = null;
  } else if (path === 'scenes') {
    state.scene = null;
    mirror.scenes = { recalledSceneId: '', favoriteSceneIds: [] };
    favoriteSceneIds = [];
  } else if (!['meta', 'panel/meta', 'core'].includes(path)) {
    return { model: current, kind: 'invalid', error: `Unknown authored node ${path}.` };
  }

  state.stateRevision = Math.max(state.stateRevision ?? 0, revision);
  return {
    kind: 'applied',
    model: {
      ...current,
      state,
      mirror,
      revisions,
      favoriteSceneIds,
    },
  };
}

/**
 * Adapt independent tree-node observations to the same room-shaped model installed by VenueProvider.
 * This is intentionally a thin wrapper over the production reducer: the parity harness can replay a
 * TreeEmitter capture without growing a second state implementation in test code.
 */
export function createAuthoredNodeObserver(rig: Readonly<RigSnapshot>): AuthoredNodeObserver {
  let model: AuthoredTreeModel = { mirror: {}, revisions: {} };
  let lastChange: Record<string, unknown> | undefined;
  let latestRevision = -1;

  const install = (path: string, reduction: AuthoredTreeReduction): void => {
    if (reduction.kind === 'invalid') throw new Error(reduction.error ?? `Invalid authored node at ${path}.`);
    if (reduction.kind === 'applied') model = reduction.model;
  };

  return {
    observe(path, node) {
      if (node == null) {
        // RTDB deletion callbacks do not carry an envelope. Advancing this path by one preserves the
        // production stale-write guard while giving the capture's ordered observation a revision.
        const revision = Math.max(latestRevision, model.revisions[path] ?? -1) + 1;
        latestRevision = revision;
        install(path, removeAuthoredTreeNode(model, path, revision, rig as RigSnapshot));
        return;
      }
      install(path, reduceAuthoredTreeNode(model, path, {
        schema: AUTHORED_NODE_SCHEMA,
        revision: node.revision,
        value: node.value,
        lastChange: node.lastChange,
      }, rig as RigSnapshot));
      const nestedLastChange = isRecord(node.value) && isRecord(node.value.lastChange)
        ? node.value.lastChange
        : undefined;
      if (node.revision >= latestRevision && (node.lastChange || nestedLastChange)) {
        latestRevision = node.revision;
        lastChange = structuredClone(node.lastChange ?? nestedLastChange);
      } else {
        latestRevision = Math.max(latestRevision, node.revision);
      }
    },
    snapshot() {
      if (!model.state) return undefined;
      const state = structuredClone(model.state);
      const mirror = structuredClone(model.mirror);
      return {
        ...mirror,
        ...state,
        panelFixtures: structuredClone(model.panelFixtures ?? state.fixtures),
        ...(lastChange ? { lastChange: structuredClone(lastChange) } : {}),
      };
    },
  };
}
